/* eslint-disable array-bracket-spacing */
import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { generatePath, useLocation, useParams } from 'react-router-dom';
import { SaveOutlined, StopOutlined } from '@ant-design/icons';
import BaseModal from '@components/common/form/BaseModal';
// import styles from './index.module.scss';
import ListPage from '@components/common/layout/ListPage';
import PageWrapper from '@components/common/layout/PageWrapper';
import { DEFAULT_TABLE_ITEM_SIZE_LARGE, EMPTY_DETAIL } from '@constants';
import apiConfig from '@constants/apiConfig';
import { codeThirdParty } from '@constants/masterData';
import useListModal from '@hooks/useListModal';
import useTranslate from '@hooks/useTranslate';
import { BaseTable } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import ThirdPartyForm from '@modules/restaurant/ThirdParty/ThirdPartyForm';
import routes from '@routes';
import { Button, Flex } from 'antd';

const message = defineMessages({
    objectName: 'Third Party',
});

const FORM_ID = 'form-digibes-third-party';

const ThirdPartyListPage = () => {
    const translate = useTranslate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const restaurantId = queryParams.get('restaurantId');
    const restaurantName = queryParams.get('restaurantName');
    const params = useParams();

    const {
        data,
        mixinFuncs,
        queryFilter,
        loading,
        pagination,
        detail,
        loadingSave,
        openModal,
        onCancelModal,
        setIsChangedFormValues,
        onSave,
        isEditing,
        isChanged,
        titleModal,
    } = useListModal({
        apiConfig: {
            getList: apiConfig.thirdParty.getList,
            getById: apiConfig.thirdParty.getById,
            create: apiConfig.thirdParty.create,
            update: apiConfig.thirdParty.update,
            delete: apiConfig.thirdParty.delete,
            changeStatus: apiConfig.thirdParty.update,
        },
        options: {
            pageSize: DEFAULT_TABLE_ITEM_SIZE_LARGE,
            objectName: translate.formatMessage(message.objectName),
        },
        override: (funcs) => {
            funcs.mappingData = (response) => {
                if (response.result === true) {
                    return {
                        data: response.data.content,
                        total: response.data.totalElements,
                    };
                }
            };
        },
    });

    const handleSubmitForm = (values) => {
        const preparedData = isEditing
            ? { ...values, id: detail?.id, status: 1 }
            : { ...values, status: 1, restaurantId };
        onSave(preparedData);
    };

    const columns = [
        {
            title: <FormattedMessage defaultMessage="Code" />,
            dataIndex: 'code',
            width: 200,
            render: (value) => {
                const item = codeThirdParty.find((i) => i.value == value);
                return item ? item.label : null;
            },
        },
        {
            title: <FormattedMessage defaultMessage="Api key" />,
            dataIndex: ['apiKey'],
        },
        mixinFuncs.renderActionColumn(
            {
                edit: true,
                delete: true,
            },
            { width: '120px' },
        ),
    ];

    return (
        <PageWrapper
            routes={[
                {
                    breadcrumbName: translate.formatMessage(commonMessage.digibesShop),
                    path: routes.shopAccountListPage.path,
                },
                {
                    breadcrumbName: translate.formatMessage(commonMessage.restaurant),
                    path: `${generatePath(routes.RestaurantByCustomerListPage.path, {
                        customerId: params.customerId,
                    })}`,
                },
                { breadcrumbName: translate.formatMessage(message.objectName) },
            ]}
        >
            <ListPage
                title={<span style={{ fontSize: '17px', paddingBottom: 8, height: 100 }}>{restaurantName}</span>}
                searchForm={mixinFuncs.renderSearchForm({ initialValues: queryFilter })}
                actionBar={mixinFuncs.renderActionBar()}
                baseTable={
                    <BaseTable
                        onChange={mixinFuncs.changePagination}
                        columns={columns}
                        dataSource={data}
                        loading={loading}
                        pagination={pagination}
                    />
                }
            />
            {openModal && (
                <BaseModal
                    isModalVisible={openModal}
                    isEditing={isEditing}
                    handleCancel={onCancelModal}
                    title={titleModal}
                    width="800px"
                    noBodyPadding={true}
                    customFooter={
                        <Flex justify="end" gap={8} style={{ padding: '8px 16px' }}>
                            <Button danger onClick={onCancelModal} icon={<StopOutlined />}>
                                {translate.formatMessage(commonMessage.cancel)}
                            </Button>
                            <Button
                                type="primary"
                                htmlType="submit"
                                form={FORM_ID}
                                loading={loadingSave}
                                disabled={isEditing && !isChanged}
                                icon={<SaveOutlined />}
                            >
                                {translate.formatMessage(isEditing ? commonMessage.update : commonMessage.save)}
                            </Button>
                        </Flex>
                    }
                >
                    <ThirdPartyForm
                        id={restaurantId}
                        formId={FORM_ID}
                        dataDetail={detail || EMPTY_DETAIL}
                        isEditing={isEditing}
                        actions={null}
                        onSubmit={handleSubmitForm}
                        setIsChangedFormValues={setIsChangedFormValues}
                    />
                </BaseModal>
            )}
        </PageWrapper>
    );
};

export default ThirdPartyListPage;
